import { useCallback, useEffect, useRef, useState } from "react";
import { apiMain } from "@/common/services";
import { IGif } from "@/common/services/types";
import { EventSource, metricsBus } from "@/common/buses";
import {
  EXPERIMENT_CONTEXT_BASED_RECOMMENDATIONS,
  EXPERIMENT_COVERAGE_MAP,
  VARIANT_CONTROL,
  VARIANT_TREATMENT
} from "@/common/services/experiment/constants";
import { useGifStore } from "@/common/store/gifStore";
import { useUsersStore } from "@/common/store/storeUsers";
import { useNichesStore } from "@/common/store/storeNiches";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { useLocation } from "react-router";
import experimentService from "@/common/services/experiment/experimentService";

interface IProps {
  position: number;
  gif: IGif;
  isPlayerVisible: boolean;
}

export type CloseType = "scroll" | "replay" | "close";

export const useQuickRecs = ({ position, gif, isPlayerVisible }: IProps) => {
  const updateGifs = useGifStore(state => state.updateGifs);
  const updateUsers = useUsersStore(state => state.updateUsers);
  const updateNiches = useNichesStore(state => state.updateNiches);
  const eventSource = useAnalyticsStore(state => state.eventSource);
  const location = useLocation();

  const [isQuickRecsVisible, setIsQuickRecsVisible] = useState(false);
  const [quickRecsEnabled, setQuickRecsEnabled] = useState(false);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [quickRecsFailed, setQuickRecsFailed] = useState(false);
  const isRequested = useRef(false);

  useEffect(() => {
    (async () => {
      const startExperiment = location.pathname === "/" && eventSource?.name === EventSource.Main;
      const variant = startExperiment ? await experimentService.getVariant(
        EXPERIMENT_CONTEXT_BASED_RECOMMENDATIONS,
        VARIANT_CONTROL,
        EXPERIMENT_COVERAGE_MAP[EXPERIMENT_CONTEXT_BASED_RECOMMENDATIONS]
      ) : false;

      setQuickRecsEnabled(variant === VARIANT_TREATMENT && position !== 1 && !gif.cta);
    })();
  }, [eventSource, location, position, gif]);

  const closeQuickRecs = useCallback((type: CloseType) => {
    if (isQuickRecsVisible) {
      setIsQuickRecsVisible(false);
      metricsBus.emit("quickRecsClosed", { type });
    }
  }, [isQuickRecsVisible]);

  const showQuickRecs = useCallback(() => {
    if (!quickRecsEnabled || isRequested.current) {
      return;
    }

    isRequested.current = true;

    (async () => {
      try {
        const { gifs, users, niches } = await apiMain.getQuickRecs({ id: gif.id });

        if (gifs.length >= 3) {
          const filteredGifs = gifs.slice(0, 3);
          updateGifs(filteredGifs);
          updateUsers(users);
          updateNiches(niches);
          setGifs(filteredGifs);
          setIsQuickRecsVisible(true);

          metricsBus.emit("quickRecsShown");
        }
      } catch (e) {
        setQuickRecsFailed(true);
      }
    })();
  }, [gif, quickRecsEnabled]);

  useEffect(() => {
    if (isQuickRecsVisible && !isPlayerVisible) {
      closeQuickRecs("scroll");
    }
  }, [isPlayerVisible, closeQuickRecs]);

  return {
    gifs,
    quickRecsEnabled,
    quickRecsFailed,
    isQuickRecsVisible,
    closeQuickRecs,
    showQuickRecs,
  };
};
